import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { CityZipCodeField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for CityZipCode
const FIELD_MAPPING: Record<string, string> = {
  [CityZipCodeField.KEY]: 'CITY_ZIP_CODE_KEY',
  [CityZipCodeField.CITY]: 'CITY_ZIP_CODE_CITY',
  [CityZipCodeField.CITY_NAME]: 'CITY_ZIP_CODE_CITY_NAME',
  [CityZipCodeField.COUNTY]: 'CITY_ZIP_CODE_COUNTY',
  [CityZipCodeField.STATE]: 'CITY_ZIP_CODE_STATE',
  [CityZipCodeField.ZIP]: 'CITY_ZIP_CODE_ZIP',
  [CityZipCodeField.PREFERRED_CITY]: 'CITY_ZIP_CODE_PREFERRED_CITY',
  [CityZipCodeField.MODIFICATION_TIMESTAMP]: 'CITY_ZIP_MODIFICATION_TIMESTAMP',
};

export class CityZipCodeResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // CityZipCodes don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // CityZipCodes are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // CityZipCodes are not directly related to listings
  }

  // Utility methods for city zip code resources
  getCity(): string | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.CITY]];
  }

  getCityName(): string | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.CITY_NAME]];
  }

  getZipCode(): string | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.ZIP]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.COUNTY]];
  }

  getState(): string | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.STATE]];
  }

  isPreferredCity(): boolean | undefined {
    return this[FIELD_MAPPING[CityZipCodeField.PREFERRED_CITY]];
  }
}

export class CityZipCodeResourceManager extends BaseResourceManager<CityZipCodeResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(
      ResoResource.CITY_ZIP_CODE,
      executeQuery,
      fieldTranslator,
      taskManager,
      {
        ...options,
        useTranslation: false, // Disable translation
      },
    );
  }

  /**
   * Map field name using our custom mapping
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to CityZipCodeResource
   */
  protected mapFromApiCore(apiData: any): CityZipCodeResource {
    return new CityZipCodeResource(apiData);
  }

  /**
   * Core mapping function from CityZipCodeResource to API data
   */
  protected mapToApiCore(resource: CityZipCodeResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find city zip codes by city name
   */
  public async findByCityName(
    cityName: string,
  ): Promise<CityZipCodeResource[]> {
    return this.findListByField(CityZipCodeField.CITY_NAME, cityName);
  }

  /**
   * Find city zip codes by zip code
   */
  public async findByZipCode(zip: string): Promise<CityZipCodeResource[]> {
    return this.findListByField(CityZipCodeField.ZIP, zip);
  }

  /**
   * Find city zip codes by county
   */
  public async findByCounty(county: string): Promise<CityZipCodeResource[]> {
    return this.findListByField(CityZipCodeField.COUNTY, county);
  }

  /**
   * Find city zip codes by state
   */
  public async findByState(state: string): Promise<CityZipCodeResource[]> {
    return this.findListByField(CityZipCodeField.STATE, state);
  }

  /**
   * Find city zip codes by state and county
   */
  public async findByStateAndCounty(
    state: string,
    county: string,
  ): Promise<CityZipCodeResource[]> {
    return this.findListByFields({
      [CityZipCodeField.STATE]: state,
      [CityZipCodeField.COUNTY]: county,
    });
  }
}

/**
 * Create a new CityZipCodeResource instance with required fields
 */
export function createCityZipCodeResource(
  data: Partial<Record<string, any>> = {},
): CityZipCodeResource {
  const defaults = {};

  return new CityZipCodeResource({
    ...defaults,
    ...data,
  });
}
